import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, Table, Input, Button, Tag, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import { ItemBlockStoreStatus } from '../../types/itemblocktypes';

const { Search } = Input;

interface Props {
    visible: boolean;
    onClose: () => void;
    onOk: (selectedStoreGuids: string[], action: 'block' | 'unblock') => void;
    itemName: string;
    stores: ItemBlockStoreStatus[];
    loading?: boolean;
    action: 'block' | 'unblock';
}

const BlockStoreSelectorDrawer: React.FC<Props> = ({
    visible,
    onClose,
    onOk,
    itemName,
    stores,
    loading,
    action,
}) => {
    const [searchText, setSearchText] = useState('');
    const [filteredStores, setFilteredStores] = useState<ItemBlockStoreStatus[]>(stores);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const debouncedSearch = useCallback(
        debounce((value: string) => setSearchText(value), 300),
        []
    );

    useEffect(() => {
        let filtered = stores;
        // Default filter by action ("block" shows Available; "unblock" shows Blocked)
        filtered =
            action === 'block'
                ? filtered.filter(s => s.status === 'Available')
                : filtered.filter(s => s.status === 'Blocked');

        if (searchText) {
            filtered = filtered.filter(s => s.storeNumber.toLowerCase().includes(searchText.toLowerCase()));
        }
        setFilteredStores(filtered);
        setCurrentPage(1);
    }, [stores, searchText, action]);

    const columns: ColumnsType<ItemBlockStoreStatus> = [
        { title: 'Store Number', dataIndex: 'storeNumber', key: 'storeNumber' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: 'Available' | 'Blocked') =>
                status === 'Available' ? <Tag color="green">Available</Tag> : <Tag color="red">Blocked</Tag>,
        },
    ];

    return (
        <Drawer
            title={`${itemName} - ${action === 'block' ? 'Block' : 'Unblock'} Stores`}
            width={600}
            onClose={onClose}
            open={visible}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px',
                },
            }}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => onOk(selectedRowKeys, action)}
                        disabled={!selectedRowKeys.length}
                        danger={action === 'block'}
                        style={{ marginRight: 8, fontSize: '12px' }}
                    >
                        {action === 'block' ? 'Block' : 'Unblock'}
                    </Button>
                </div>
            }
        >
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Spin size="default" />
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <Search
                            placeholder="Search stores"
                            allowClear
                            value={searchText}
                            onChange={e => debouncedSearch(e.target.value)}
                            style={{ flex: 1 }}
                        />
                    </div>

                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total Stores: {filteredStores.length}</span>
                        <span>Selected: {selectedRowKeys.length}</span>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Button onClick={() => setSelectedRowKeys(filteredStores.map(s => s.storeGuid))} style={{ marginRight: 8 }}>
                            Select All
                        </Button>
                        <Button onClick={() => setSelectedRowKeys([])}>Deselect All</Button>
                    </div>

                    <Table<ItemBlockStoreStatus>
                        rowSelection={{
                            selectedRowKeys,
                            onChange: keys => setSelectedRowKeys(keys as string[]),
                        }}
                        dataSource={filteredStores}
                        columns={columns}
                        rowKey="storeGuid"
                        pagination={{
                            current: currentPage,
                            pageSize,
                            total: filteredStores.length,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20', '50'],
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            },
                        }}
                    />
                </>
            )}
        </Drawer>
    );
};

export default BlockStoreSelectorDrawer;